import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../hooks";
import { useTableV2State } from "../../../../components/TableV2/hooks/useTableV2State";
import { TABLE_CACHING_KEYS } from "../../../../constants/constants";
import { useBillingCompanyContext } from "../../../../providers";

import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../components/TableV2/interfaces/IDataTableV2Props";
import { LoadState } from "../../../../constants/enums";
import { Box, Button, Typography } from "@mui/material";

import { CustomChip } from "../../../../components/CustomChip";
import { DetailInfoCard } from "../../../../components/DetailInfoCard/DetailInfoCard";
import {
  PersonOutline,
  PhoneOutlined,
  CurrencyRupeeOutlined,
  EmailOutlined,
  EventNoteOutlined,
  SupportAgentOutlined,
  SourceOutlined,
  FlagOutlined,
  PeopleRounded,
} from "@mui/icons-material";
import { StandardTableActions } from "../../../../components/StandardTableActions/StandardTableActions";
import { DataTableV2 } from "../../../../components/TableV2/DataTableV2";
import { PageContainer } from "../../../../components/container/PageContainer";
import { fetchLeadPaymentDetailsListAsync } from "../../../../redux/paymentDetails/paymentDetailsActions";
import { PaymentDetailsDialog } from "./PaymentDetailsDialog";
import { IPaymentDetails } from "../../../../redux/paymentDetails/paymentDetails.types";
import { AddOutlined } from "@mui/icons-material";
import { IndianPriceFormatter } from "../../../../helpers/formatWithCommas";
import { ILead, IMultipleEvent } from "../../../../redux/leads/leads.types";
import { PaymentPreview } from "./components/PaymentPreview";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
export const LeadPaymentDetailsList: React.FC<{
  leadUUID: string;
  multipleEventsUUID?: string;
  initialLeadToRoughEstimate?: ILead;
  selectedEventLeadInfo?: IMultipleEvent[];
}> = ({
  leadUUID,
  multipleEventsUUID = "",
  initialLeadToRoughEstimate,
  selectedEventLeadInfo,
}) => {
    const { list, loading } = useSelector(
      (storeState: IStoreState) => storeState.payment_details.list,
    );

    const isLeadInactive =
      initialLeadToRoughEstimate?.actual_status === "DEAD" ||
      initialLeadToRoughEstimate?.actual_status === "CANCELLED";

    const [openView, setOpenView] = React.useState<string | null>(null);

    const {
      state: { searchState, columnsConfig, pagination, masterColumnResize },

      setSearchState,
      setColumnVisibility,
      onPageChange,
      onRowsPerPageChange,
      onResizeMasterColumns,
      initialColumnVisibility,
    } = useTableV2State({
      filtersInitialState: {
        defaultDateRange: "last30Days",
        selectedTab: "ACTIVE",
      },
      pagination: {
        pageNumber: 1,
        rowsInPerPage: 25,
        apiFetchRowCount: 26,
        extraFetchFactor: 1,
      },
      cachingFilters: {
        uniqueKey: TABLE_CACHING_KEYS.LEAD_PAYMENT_DETAILS,
      },
    });

    const dispatch = useDispatchWrapper();
    const { ftDate } = useTimezoneFormatter();
    const companyOutlet = useBillingCompanyContext();
    const [openEdit, setOpenEdit] = React.useState<boolean>(false);

    // Fetch payment details for all events of the lead group via multiple_events_uuid
    const fetchList = React.useCallback(() => {
      if (!multipleEventsUUID) return;
      dispatch(
        fetchLeadPaymentDetailsListAsync(
          pagination.pageNumber,
          pagination.rowsInPerPage,
          pagination.apiFetchRowCount,
          searchState,
          companyOutlet.comapny.company_outlet_uuid,
          "",
          multipleEventsUUID,
        ),
      );
    }, [
      dispatch,
      pagination.pageNumber,
      pagination.rowsInPerPage,
      pagination.apiFetchRowCount,
      searchState,
      companyOutlet.comapny.company_outlet_uuid,
      multipleEventsUUID,
    ]);

    React.useEffect(() => {
      fetchList();
    }, [fetchList]);

    const PaymentDetailsTableProps: IDataTableV2Props = {
      isPagination: true,
      // Render the table at full height (no inner vertical scroll); the header
      // pins to the page and horizontal overflow uses the pinned bottom strip.
      stickyHeader: true,

      rowsPerPageOptions: pagination.rowsInPerPage,
      isDataLoading: loading !== LoadState.Loaded,
      extraFetchFactor: pagination.extraFetchFactor,
      // selectionMode: "multiple",
      // uniqueRowKeyName: "id",
      tableCommandBarProps: {
        preDefinedPlugins: {
          refresh: {
            onClick: () => fetchList(),
          },
        },
        leftItems: {
          customPlugins: [],
        },
        rightItems: {
          customPlugins: [
            {
              key: "add-payment-details",
              onRender: () => (
                <Button
                  variant="contained"
                  size="small"
                  disabled={isLeadInactive}
                  onClick={() => setOpenEdit(true)}
                  startIcon={<AddOutlined />}
                >
                  Add Payment Details
                </Button>
              ),
            },
          ],
        },
      },
      mobileColumns: [
        {
          variant: "template1",
          headingColumn: {
            key: "mode_of_payment",
            fieldName: "mode_of_payment",
          },
          rightHeadingColumn: {
            key: "amount",
            fieldName: "amount",
          },
          subSectionColumn: [],
          otherColumns: [
            {
              key: "tax_amount_percentage",
              headerName: "Tax %",
              fieldName: "tax_amount_percentage",
            },
            {
              key: "tax_amount",
              headerName: "Tax Amount",
              fieldName: "tax_amount",
            },
            {
              key: "effective_amount",
              headerName: "Effective Amount",
              fieldName: "effective_amount",
            },
            {
              key: "total_amount_paid",
              headerName: "Total Paid",
              fieldName: "total_amount_paid",
            },
            {
              key: "total_tax_amount_paid",
              headerName: "Total Tax Paid",
              fieldName: "total_tax_amount_paid",
            },
          ],
        },
      ],

      masterColumns: [
        {
          key: "view",
          headerName: "Actions",
          fieldName: "",
          renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
          isFirstColumnSticky: true,

          onRowCellRender: (value, row: IPaymentDetails) => {
            return (
              <Box sx={{ display: "flex", gap: 2 }}>
                <StandardTableActions
                  onDownloadPreview={() =>
                    setOpenView(row.payment_transaction_uuid)
                  }
                />
              </Box>
            );
          },
        },
        {
          key: "payment_date",
          headerName: "Payment Date",
          fieldName: "payment_date",
          renderType: DataTableV2RowRenderType.DATE,
          onRowCellRender: (value, row) => {
            return <>{ftDate(row.payment_date) || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "transaction_effect",
          headerName: "Transaction Type",
          fieldName: "transaction_effect",

          enableSorting: true,

          renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
          width: "120px",

          onRowCellRender: (value, row: IPaymentDetails) => {
            return (
              <>
                {row.transaction_effect === "-" && (
                  <CustomChip color={"error"} content={"Debited"} size="small" />
                )}
                {row.transaction_effect === "+" && (
                  <CustomChip
                    color={"success"}
                    content={"Credited"}
                    size="small"
                  />
                )}

                {row.transaction_effect === "*" && (
                  <CustomChip
                    color={"info"}
                    content={"Adjustment"}
                    size="small"
                  />
                )}
              </>
            );
          },
        },
        {
          key: "payment_type",
          headerName: "Payment Type",
          fieldName: "payment_type",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{row.payment_type || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "amount",
          headerName: "Amount",
          fieldName: "amount",
          type: "currency",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{IndianPriceFormatter(row.amount) || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "tax_amount_percentage",
          headerName: "Tax %",
          type: "number",
          fieldName: "tax_amount_percentage",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{row.tax_amount_percentage || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "tax_amount",
          headerName: "Tax Amount",
          fieldName: "tax_amount",
          type: "currency",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{IndianPriceFormatter(row.tax_amount) || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "effective_amount",
          headerName: "Total Paid",
          type: "currency",
          fieldName: "effective_amount",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{IndianPriceFormatter(row.effective_amount) || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "remaining_balance",
          headerName: "Remaining Balance",
          fieldName: "remaining_balance",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{IndianPriceFormatter(row.remaining_balance) || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },
        {
          key: "billing_to",
          headerName: "Billing To",
          fieldName: "billing_to",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return (
              <>
                {row.billing_to === "BILL_TO_COMPANY" && (
                  <CustomChip
                    color={"info"}
                    content={"Bill To Company"}
                    size="small"
                  />
                )}
                {row.billing_to === "DIRECT" && (
                  <CustomChip color={"success"} content={"Direct"} size="small" />
                )}
              </>
            );
          },
          enableSorting: true,
          width: "120px",
        },

        {
          key: "mode_of_payment",
          headerName: "Payment Mode",
          fieldName: "mode_of_payment",
          renderType: DataTableV2RowRenderType.TEXT,
          onRowCellRender: (value, row) => {
            return <>{row.mode_of_payment || "---"}</>;
          },
          enableSorting: true,
          width: "120px",
        },

        {
          key: "remarks",
          headerName: "Remarks",
          fieldName: "remarks",
          renderType: DataTableV2RowRenderType.DESCRIPTION,
          turncateLength: 50,
          enableSorting: true,
          width: "200px",
        },
      ],

      masterColumnResize: {
        values: masterColumnResize,
        onResize: onResizeMasterColumns,
      },

      rows: list,

      onPageChange: onPageChange,
      onRowsPerPageChange: onRowsPerPageChange,
    };

    const handleCloseEdit = () => {
      setOpenEdit(false);
    };

    const handleSaveEdit = () => {
      setOpenEdit(false);
      fetchList();
    };

    return (
      <>
        <PageContainer
          title="View Payment Details"
          description="this is innerpage"
        >
          {initialLeadToRoughEstimate && (
            <Box
              sx={{
                mb: 2,
                width: "100%",
                minWidth: 0,
                borderRadius: 2.4,
                background: "#fff",
              }}
            >
              <Typography
                variant="h2"
                fontSize={{
                  xs: "0.75rem",
                  sm: "0.9rem",
                  md: "1.1rem",
                  lg: "1.2rem",
                }}
                fontWeight={"700"}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                  minWidth: 0,
                  padding: 1.5,
                }}
              >
                Payment Details
              </Typography>

              {/* Lead Details summary (outside the events table) */}
              <Box sx={{ pb: 1 }}>
                <DetailInfoCard
                  columns={{ xs: 6, sm: 4, md: 3 }}
                  fields={[
                    // Row 1: Guest Name | Contact Number | Email | Enquiry Date
                    {
                      label: "Guest Name",
                      icon: <PersonOutline sx={{ fontSize: 16 }} />,
                      value:
                        initialLeadToRoughEstimate.name_of_guest || "--",
                    },
                    {
                      label: "Contact Number",
                      icon: <PhoneOutlined sx={{ fontSize: 16 }} />,
                      value:
                        initialLeadToRoughEstimate.contact_number_one || "--",
                    },
                    {
                      label: "Email",
                      icon: <EmailOutlined sx={{ fontSize: 16 }} />,
                      value: initialLeadToRoughEstimate.email || "--",
                    },
                    {
                      label: "Enquiry Date",
                      icon: <EventNoteOutlined sx={{ fontSize: 16 }} />,
                      value: initialLeadToRoughEstimate.date_of_visit
                        ? ftDate(initialLeadToRoughEstimate.date_of_visit)
                        : "--",
                    },
                    // Row 2: Handled By Employee | Source | Priority | Grand Total
                    {
                      label: "Handled By Employee",
                      icon: <SupportAgentOutlined sx={{ fontSize: 16 }} />,
                      value:
                        initialLeadToRoughEstimate.handled_by_employee || "--",
                    },
                    {
                      label: "Source",
                      icon: <SourceOutlined sx={{ fontSize: 16 }} />,
                      value: initialLeadToRoughEstimate.source || "--",
                    },
                    {
                      label: "Priority",
                      icon: <FlagOutlined sx={{ fontSize: 16 }} />,
                      value: initialLeadToRoughEstimate.priority || "--",
                    },
                    {
                      label: "Total Guest",
                      icon: <PeopleRounded sx={{ fontSize: 16 }} />,
                      value: initialLeadToRoughEstimate.number_of_guests || "--",
                    },
                  ]}
                />
              </Box>

              <Box sx={{ width: "100%", minWidth: 0, overflowX: "visible" }}>
                <DataTableV2 {...PaymentDetailsTableProps} />
              </Box>
            </Box>
          )}

          {openEdit && leadUUID && (
            <PaymentDetailsDialog
              open={openEdit}
              leadUUID={leadUUID}
              multipleEventsUUID={multipleEventsUUID}
              onClose={handleCloseEdit}
              onComplete={handleSaveEdit}
              isAdvance={false}
              isCancellation={false}
            />
          )}

          {openView && (
            <PaymentPreview
              open={true}
              UUID={openView}
              onClose={() => setOpenView(null)}
            />
          )}
        </PageContainer>
      </>
    );
  };
